import { FC, useEffect, useState } from 'react'
import { useTheme } from '../../../../../../_metronic/partials/layout/app-theme/ThemeContext'
import Table from '../../common/Table'
import LedgerTableHeader from './LedgerTableHeader'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../../../redux/store'
import { getClientLogByIdApi, getClientByIdApi } from '../../../../../redux/slices/ClientSlice'
import { toast } from 'react-toastify'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'

const ClientTable: FC = () => {
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchKey, setSearchKey] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [selectedRow, setSelectedRow] = useState([])
  const [clientData, setClientData] = useState<any>(null)
  const { theme } = useTheme()
  const { id } = useParams()
  const dispatch = useDispatch<AppDispatch>()

  const clientLogRoot = useSelector((state: RootState) => state.client.ClientLog?.data || {})
  const clientFromStore = useSelector((state: RootState) => state.client.client?.data || {})

  // Handle nested structure: payload.data may contain { data: {...} }
  const clientLogData = (clientLogRoot as any)?.data || clientLogRoot

  // Use client data from ledger API if available, otherwise use client API data
  const hasLedgerData = clientLogData && Object.keys(clientLogData).length > 0
  const activeClientData = hasLedgerData ? clientLogData : clientData || clientFromStore

  const clientName = activeClientData?.name || 'Unknown Client'
  const profile_image_url = activeClientData?.profile_image_url || activeClientData?.profile_image || null
  const openingPoints = activeClientData?.opening_points_balance ?? activeClientData?.wallet_points ?? 0
  const openingMoney = activeClientData?.opening_money_balance ?? activeClientData?.wallet_money ?? 0
  const closingPoints = activeClientData?.closing_points_balance ?? activeClientData?.wallet_points ?? 0
  const closingMoney = activeClientData?.closing_money_balance ?? activeClientData?.wallet_money ?? 0
  const fromDate = activeClientData?.from_date || new Date().toLocaleDateString('en-IN')
  const toDate = activeClientData?.to_date || new Date().toLocaleDateString('en-IN')
  const logs = clientLogData?.logs || []
  const clientTotal = logs.length

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchQuery(searchKey)
    }, 1000)
    return () => clearTimeout(debounceTimeout)
  }, [searchKey])

  const fetchClientLog = async () => {
    setLoading(true)
    try {
      const logResponse = await dispatch(getClientLogByIdApi(id))

      // If ledger data is null or empty, fetch client data separately
      const ledgerData = (logResponse.payload as any)?.data
      if (!ledgerData || Object.keys(ledgerData).length === 0 || !ledgerData.name) {
        const clientResponse = await dispatch(getClientByIdApi(id))
        setClientData((clientResponse.payload as any)?.data)
      }

      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error('Failed to fetch client data')
    }
  }

  useEffect(() => {
    fetchClientLog()
  }, [currentPage, rowsPerPage, debouncedSearchQuery])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setCurrentPage(page)
    setRowsPerPage(newPerPage)
  }

  const columnData = [
    {
      name: 'Date & Time',
      cell: (row: any) => {
        const location = row?.scanned_location_at || '-'
        const formattedDate = row?.date
          ? new Date(row.date).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : '-'
        const formattedTime = row?.time
          ? new Date(`1970-01-01T${row.time}Z`).toLocaleTimeString('en-IN', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true,
            })
          : '-'
    
        return (
          <div className='d-flex flex-column py-2'>
            <div className='mb-1 d-flex align-items-center'>
              <i className='bi bi-geo-alt-fill text-info me-2' />
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip id={`tooltip-location-${location}`}>{location}</Tooltip>}
              >
                <span className='text-dark fw-semibold fs-7 text-truncate' style={{maxWidth: 180}}>
                  {location}
                </span>
              </OverlayTrigger>
            </div>
            <div className='mb-1 d-flex align-items-center'>
              <i className='bi bi-calendar-check-fill text-primary me-2' />
              <span className='text-dark fw-semibold fs-7'>{formattedDate}</span>
            </div>
            <div className='d-flex align-items-center'>
              <i className='bi bi-clock-fill text-muted me-2' />
              <span className='text-muted fw-semibold fs-7'>{formattedTime}</span>
            </div>
          </div>
        )
      },
      minWidth: '180px',
      sortable: true,
    },
    {
      name: 'Transaction Info',
      cell: (row: any) => {
        const status = row?.status || '-'
        const statusColor =
          status === 'approved'
            ? 'success'
            : status === 'rejected'
            ? 'danger'
            : status === 'pending'
            ? 'warning'
            : 'secondary'

        return (
          <div className='d-flex flex-column py-2'>
            <div className='mb-1'>
              <i className='bi bi-tag-fill text-primary me-2' />
              <span className='text-dark fw-semibold fs-7'>
                {row?.type ? row.type.charAt(0).toUpperCase() + row.type.slice(1) : '-'}
              </span>
            </div>
            <div className='mb-1'>
              <i className='bi bi-info-circle-fill text-muted me-2' />
              <span className={`badge badge-${statusColor} fw-semibold`} style={{fontSize: '0.7rem'}}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </span>
            </div>
            {row?.coupon_code && (
              <div>
                <i className='bi bi-ticket-perforated text-muted me-2' />
                <span className='text-muted fw-semibold fs-7'>{row.coupon_code}</span>
              </div>
            )}
          </div>
        )
      },
      minWidth: '200px',
      sortable: true,
    },
    {
      name: 'Points Transaction',
      cell: (row: any) => (
        <div className='d-flex flex-column py-2'>
          {row?.points_debit > 0 && (
            <div className='mb-1'>
              <i className='bi bi-arrow-down-circle-fill text-danger me-2' />
              <span className='text-danger fw-semibold fs-7'>Debit: {row.points_debit}</span>
            </div>
          )}
          {row?.points_credit > 0 && (
            <div className='mb-1'>
              <i className='bi bi-arrow-up-circle-fill text-success me-2' />
              <span className='text-success fw-semibold fs-7'>Credit: {row.points_credit}</span>
            </div>
          )}
          <div>
            <i className='bi bi-wallet2 text-primary me-2' />
            <span className='text-dark fw-bold fs-7'>Balance: {row?.points_balance ?? 0}</span>
          </div>
        </div>
      ),
      minWidth: '180px',
      sortable: true,
    },
    {
      name: 'Money Transaction',
      cell: (row: any) => (
        <div className='d-flex flex-column py-2'>
          {row?.money_debit > 0 && (
            <div className='mb-1'>
              <i className='bi bi-arrow-down-circle-fill text-danger me-2' />
              <span className='text-danger fw-semibold fs-7'>
                Debit: ₹{Number(row.money_debit).toFixed(2)}
              </span>
            </div>
          )}
          {row?.money_credit > 0 && (
            <div className='mb-1'>
              <i className='bi bi-arrow-up-circle-fill text-success me-2' />
              <span className='text-success fw-semibold fs-7'>
                Credit: ₹{Number(row.money_credit).toFixed(2)}
              </span>
            </div>
          )}
          <div>
            <i className='bi bi-currency-rupee text-primary me-2' />
            <span className='text-dark fw-bold fs-7'>
              Balance: ₹{row?.money_balance ? Number(row.money_balance).toFixed(2) : '0.00'}
            </span>
          </div>
        </div>
      ),
      minWidth: '200px',
      sortable: true,
    },
  ]

  // No Data Found Component
  const NoDataFound = () => (
    <div
      className='d-flex flex-column align-items-center justify-content-center py-10'
      style={{minHeight: '400px'}}
    >
      <div
        className='d-flex align-items-center justify-content-center mb-4'
        style={{
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          backgroundColor: '#f1f3f5',
        }}
      >
        <i className='bi bi-inbox fs-1 text-muted' style={{fontSize: '4rem'}}></i>
      </div>
      <h3 className='text-gray-700 fw-bold mb-2'>No Transaction Logs Found</h3>
      <p className='text-muted fs-6 text-center mb-4' style={{maxWidth: '400px'}}>
        There are no transaction records available for this client during the selected period.
      </p>
      <div className='d-flex gap-2 align-items-center text-muted fs-7'>
        <i className='bi bi-info-circle'></i>
        <span>Transactions will appear here once the client makes any activity</span>
      </div>
    </div>
  )

  return (
    <div className='card'>
      <LedgerTableHeader
        clientName={clientName}
        fromDate={fromDate}
        toDate={toDate}
        openingPoints={String(openingPoints)}
        openingMoney={String(openingMoney)}
        closingPoints={String(closingPoints)}
        closingMoney={String(closingMoney)}
        profile_image_url={profile_image_url}
        theme='primary'
      />

      <div className='card-body p-0 shadow-sm'>
        {!loading && logs.length === 0 ? (
          <NoDataFound />
        ) : (
          <Table
            data={logs}
            columns={columnData}
            isLoading={loading}
            handlePerRowsChange={handlePerRowsChange}
            handlePageChange={handlePageChange}
            totalRows={clientTotal}
            showPagination={false}
            onRowClicked={() => {}}
            selectableRows
            onSelectedRowsChange={(selected) => {
              setSelectedRow(selected.selectedRows)
            }}
          />
        )}
      </div>
    </div>
  )
}

export default ClientTable
